import {convertImageToWebP} from '@/helpers/shadcn/utils'
import {deleteProductMediaService} from '@/services/productMedia'
import {useAppDispatch} from '@/store'
import {setToastNotifications} from '@/store/slices/commonSlice'
import {FILE, SUPPORTED_PRODUCT_MEDIA} from '@/utils/constants/commonConstants'
import {growthBookFeatureFlags} from '@/utils/constants/growthBookFeatureFlags'
import {useFeatureValue} from '@growthbook/growthbook-react'
import {useCallback, useMemo, useState} from 'react'

const initialFileErrorState = {feedbackType: 'info', helperText: ''}
const supportedProductMediaDescription = SUPPORTED_PRODUCT_MEDIA.join(', ')

export const useMediaGallery = ({productId, mediaSlots, updateMediaSlot, clearMediaSlot}) => {
  const dispatch = useAppDispatch()
  const {maxIndividualImageSize = '2MB'} = useFeatureValue(growthBookFeatureFlags.AdminBffImageValidationRules) || {}
  const [mediaSlotUiState, setMediaSlotUiState] = useState({})
  const [isDeletingFromServer, setIsDeletingFromServer] = useState(false)

  const updateMediaSlotUiState = useCallback((displayOrder, updates) => {
    setMediaSlotUiState(prev => ({
      ...prev,
      [displayOrder]: {
        uploading: false,
        error: initialFileErrorState,
        ...prev[displayOrder],
        ...updates,
      },
    }))
  }, [])

  const galleryMediaSlots = useMemo(
    () =>
      mediaSlots.map(slot => {
        const slotUiState = mediaSlotUiState[slot.displayOrder] || {}
        return {
          ...slot,
          uploading: slotUiState.uploading || false,
          error: slotUiState.error || initialFileErrorState,
        }
      }),
    [mediaSlots, mediaSlotUiState]
  )

  const handleUpload = useCallback(
    async (displayOrder, file) => {
      if (!file) return

      const maxSizeMB = Number(maxIndividualImageSize?.match(/\d+/)?.[0]) || 2
      if (file.size > maxSizeMB * FILE.SIZE_BYTES) {
        updateMediaSlotUiState(displayOrder, {
          error: {
            feedbackType: 'error',
            helperText: `Each image must be less than or equal to ${maxSizeMB} MB.`,
          },
        })
        return
      }

      try {
        updateMediaSlotUiState(displayOrder, {uploading: true, error: initialFileErrorState})

        const fileURL = URL.createObjectURL(file)
        const convertedFiles = await convertImageToWebP(file, {
          entityType: 'product',
        })

        if (convertedFiles?.metadata && convertedFiles?.webpFile) {
          const mediaMetadata = {
            ...convertedFiles.metadata,
            id: productId,
            displayOrder,
            mediaType: 'IMAGE',
            mediaTitle: 'Product',
            referenceEntity: 'product',
          }

          updateMediaSlot(displayOrder, {
            file: convertedFiles.webpFile,
            imageUri: fileURL,
            metadata: mediaMetadata,
          })
        }
      } catch (error) {
        updateMediaSlotUiState(displayOrder, {
          error: {
            feedbackType: 'error',
            helperText: `Failed to process file. Supported formats: ${supportedProductMediaDescription}`,
          },
        })
      } finally {
        updateMediaSlotUiState(displayOrder, {uploading: false})
      }
    },
    [productId, maxIndividualImageSize, updateMediaSlot, updateMediaSlotUiState]
  )

  const handleUploadError = useCallback(
    (displayOrder, error) => {
      updateMediaSlotUiState(displayOrder, {
        error: {
          feedbackType: 'error',
          helperText: error || `Invalid file format. Please use ${supportedProductMediaDescription} files only.`,
        },
      })
    },
    [updateMediaSlotUiState]
  )

  const handleDeleteImage = useCallback(
    displayOrder => {
      clearMediaSlot(displayOrder)
      updateMediaSlotUiState(displayOrder, {error: initialFileErrorState})
    },
    [clearMediaSlot, updateMediaSlotUiState]
  )

  const handleDeleteFromServer = useCallback(
    async displayOrder => {
      try {
        setIsDeletingFromServer(true)
        await deleteProductMediaService({productId, displayOrder})

        dispatch(
          setToastNotifications({
            show: true,
            message: `Image deleted successfully`,
            type: 'success',
          })
        )

        handleDeleteImage(displayOrder)
      } catch (error) {
        dispatch(
          setToastNotifications({
            show: true,
            message: error?.message || 'Failed to delete image',
            type: 'error',
          })
        )
      } finally {
        setIsDeletingFromServer(false)
      }
    },
    [productId, dispatch, handleDeleteImage]
  )

  return {
    mediaSlots: galleryMediaSlots,
    isDeletingFromServer,
    handleUpload,
    handleUploadError,
    handleDeleteImage,
    handleDeleteFromServer,
  }
}
